import type { FormContextMiniForm, UUID } from "@thalamos/aspire-common";
import {
  formIsMedRec,
  formIsVoid,
  getBaseFormTemplate,
  getFullFormName,
  getLatestVersion,
  redoneFromFormIds,
} from "@thalamos/aspire-common";

/** A med rec a doctor signed, offered as the basis of a payment request. */
export interface SignedMedRecOption {
  formId: UUID;
  formName: string;
  /** The doctor's most recent signature on the form's latest version. */
  signatureDate: string;
  isVoid: boolean;
}

/**
 * The med recs `userId` signed, to base a payment request on. A voided form is
 * left out, because its redo replacement is the form to claim against.
 * `linkedFormId` is the exception: a request already linked to a form that has
 * since been voided still shows the form it is linked to.
 */
export function signedMedRecOptions(args: {
  forms: readonly FormContextMiniForm[];
  userId: UUID;
  linkedFormId?: UUID | null;
}): SignedMedRecOption[] {
  const options: SignedMedRecOption[] = [];
  for (const form of args.forms) {
    if (!formIsMedRec(form)) continue;
    const isVoid = formIsVoid(form);
    if (isVoid && form.id !== args.linkedFormId) continue;

    const latestSignature = (getLatestVersion(form)?.signatures ?? [])
      .filter((sig) => sig.userId === args.userId)
      .map((sig) => sig.legalSignatureDate)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
    if (latestSignature === undefined) continue;

    const template = getBaseFormTemplate(form.formTemplate.id);
    options.push({
      formId: form.id,
      formName: template ? getFullFormName(template) : "Medical Recommendation",
      signatureDate: latestSignature,
      isVoid,
    });
  }
  return options;
}

/**
 * Where the doctor's existing payment request for a form sits: on the form
 * itself, or on a form it replaced through a redo.
 */
export type PriorRequestLink = "same-form" | "replaced-form";

/**
 * Whether `formId`, or a form it replaced through a redo, already has one of
 * the doctor's payment requests, and which. `requestedFormIds` are the forms
 * the doctor's other requests are linked to.
 */
export function findPriorRequestLink(args: {
  formId: UUID;
  forms: readonly FormContextMiniForm[];
  requestedFormIds: ReadonlySet<UUID>;
}): PriorRequestLink | undefined {
  if (args.requestedFormIds.has(args.formId)) return "same-form";
  const form = args.forms.find((f) => f.id === args.formId);
  if (form === undefined) return undefined;
  for (const replacedId of redoneFromFormIds({ form, forms: args.forms })) {
    if (args.requestedFormIds.has(replacedId)) return "replaced-form";
  }
  return undefined;
}

/** The option for the form that replaced `formId` through one or more redos. */
export function findRedoReplacementOption(args: {
  formId: UUID;
  options: readonly SignedMedRecOption[];
  forms: readonly FormContextMiniForm[];
}): SignedMedRecOption | undefined {
  const formsById = new Map(args.forms.map((f) => [f.id, f]));
  return args.options.find((option) => {
    const form = formsById.get(option.formId);
    return (
      form !== undefined &&
      redoneFromFormIds({ form, forms: args.forms }).has(args.formId)
    );
  });
}
